import {
  collection,
  doc,
  addDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { generateNanoId } from 'utils';
import firebaseConfig from '../../config';

const { databaseId } = firebaseConfig;

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg'
];

function SessionDocumentsFactory(firebase) {
  // Validation errors carry a `code` so the UI layer can map them to a
  // translated message (factories run outside the IntlProvider). The English
  // `message` remains as a fallback.
  const validationError = (message, code) => {
    const error = new Error(message);
    error.code = code;
    return error;
  };

  const uploadDocument = async ({ file, sessionId, user }) => {
    if (!file || file.size === 0) {
      throw validationError('This file is empty', 'file-empty');
    }
    if (file.size > MAX_FILE_SIZE) {
      throw validationError(
        'Files must be smaller than 20 MB',
        'file-too-large'
      );
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      throw validationError(
        'This file type is not supported',
        'file-type-unsupported'
      );
    }

    const documentId = generateNanoId({ length: 12 });
    const storagePath = `session-documents/${sessionId}/${documentId}_${file.name}`;
    const storageRef = ref(firebase.sessionDocumentsStorage, storagePath);

    const metadata = {
      contentType: file.type,
      customMetadata: {
        uploadedAt: new Date().toISOString(),
        sessionId,
        uploadedBy: user.uid
      }
    };

    await uploadBytes(storageRef, file, metadata);
    const downloadURL = await getDownloadURL(storageRef);

    try {
      await setDoc(
        doc(firebase.fsdb, 'sessions', sessionId, 'documents', documentId),
        {
          documentId,
          sessionId,
          fileName: file.name,
          storagePath,
          downloadURL,
          contentType: file.type,
          size: file.size,
          uploadedBy: user.uid,
          uploaderName: user.name,
          timestamp: serverTimestamp()
        }
      );
    } catch (error) {
      // Metadata write failed — remove the orphaned storage object so the
      // bucket doesn't accumulate files no UI can ever list
      deleteObject(storageRef).catch((cleanupError) =>
        console.error('Error cleaning up orphaned upload:', cleanupError)
      );
      throw error;
    }

    return documentId;
  };

  const subscribeToSessionDocuments = ({ sessionId, snapshot }) => {
    const documentsRef = collection(
      firebase.fsdb,
      'sessions',
      sessionId,
      'documents'
    );
    return onSnapshot(
      query(documentsRef, orderBy('timestamp', 'asc')),
      snapshot
    );
  };

  const deleteDocument = async ({ sessionId, document }) => {
    // Delete the storage object first, but never let a storage failure leave a
    // dangling metadata doc (a dangling storage object is harmless; a dangling
    // doc renders a dead link)
    try {
      await deleteObject(
        ref(firebase.sessionDocumentsStorage, document.storagePath)
      );
    } catch (error) {
      console.error('Error deleting document file from storage:', error);
    }
    return deleteDoc(
      doc(
        firebase.fsdb,
        'sessions',
        sessionId,
        'documents',
        document.documentId
      )
    );
  };

  const recordDocumentDownload = async ({
    sessionId,
    documentId,
    fileName,
    user
  }) => {
    try {
      await addDoc(collection(firebase.fsdb, 'documentDownloads'), {
        sessionId,
        documentId,
        fileName,
        uid: user.uid,
        name: user.name,
        timestamp: serverTimestamp(),
        databaseId
      });
    } catch (error) {
      // Fire-and-forget: recording must never block the download itself
      console.error('Error recording document download:', error);
    }
  };

  return {
    uploadDocument,
    subscribeToSessionDocuments,
    deleteDocument,
    recordDocumentDownload
  };
}

export default SessionDocumentsFactory;
